import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiZap, FiCheck, FiX, FiRefreshCw, FiChevronLeft, FiChevronDown, FiChevronRight, FiAlertTriangle, FiLock, FiBell, FiCopy, FiSettings, FiLink, FiActivity, } from 'react-icons/fi';
import composioClient, { type ConnectedApp, type AvailableApp, type EmailTriggerStatus, type EmailTriggerProvider, type TriggerEvent, AVAILABLE_APPS, isAppVisible, TOOLKITS_REQUIRING_OAUTH_SETUP, COMPOSIO_OAUTH_CALLBACK_URLS, getOAuthSetupGuide, isOAuthSetupRequiredError, normalizeOAuthClientId, validateOAuthClientSecret, createProject, enableEmailTrigger, disableEmailTrigger, getEmailTriggerStatus, getTriggerEvents, subscribeTriggerEvents, listOAuthApps, saveOAuthAppCredentials, } from '../../services/composioClient';
import { getLightspeedSettings, saveLightspeedSettings, removeLightspeedSettings, } from '../../services/lightspeedVoiceService';
import { getGteVoiceStatus, enableGteVoice, disableGteVoice, } from '../../services/gteVoiceService';
import { getMomentumVoiceStatus, enableMomentumVoice, disableMomentumVoice, getMomentumVoiceAgentStates, setMomentumVoiceAgentState, getMomentumVoiceAgents, } from '../../services/momentumVoiceService';
import { getMomentumSmsStatus, setMomentumSmsState } from '../../services/momentumSmsService';
import { checkEmailIntegration, listEmailIntegrations, setEmailAiAutomation, isAiSupportedIntegration, EmailExchangeNotConfiguredError, type MacEmailIntegration, } from '../../services/centralizedEmailService';
import IntegrationActivityModal from './IntegrationActivityModal';
import type { ActivitySource } from '../../services/integrationActivityService';
import '../../styles/Settings.css';
import './IntegrationsPage.css';
const { FiEye, FiEyeOff } = require('react-icons/fi') as {
    FiEye: React.FC<{
        size?: number;
    }>;
    FiEyeOff: React.FC<{
        size?: number;
    }>;
};
interface AppCardProps {
    app: AvailableApp;
    connectedApp?: ConnectedApp;
    onConnect: (appId: string) => void;
    onDisconnect: (appId: string) => void;
    onConfirmDisconnect: (appId: string) => void;
    onCancelDisconnect: () => void;
    onToggleTrigger?: (appId: string, enable: boolean) => void;
    onOpenOAuthSetup?: (appId: string) => void;
    onOpenLsvSetup?: () => void;
    isConnecting: boolean;
    isDisconnecting: boolean;
    isConfirming: boolean;
    keyMissing?: boolean;
    needsOAuthSetup?: boolean;
    /** Name of the app whose connection this tile uses (Outlook Calendar -> Outlook 365). */
    linkedAppName?: string;
    /** Names of tiles that use this app's connection; they go away with it. */
    linkedDependents?: string;
    isLsvSetup?: boolean;
    lsvConnected?: boolean;
    triggerEnabled?: boolean;
    triggerLoading?: boolean;
    isGteToggle?: boolean;
    gteEnabled?: boolean;
    gteToggleLoading?: boolean;
    onGteToggle?: (enable: boolean) => void;
    isMvSettings?: boolean;
    mvEnabled?: boolean;
    mvToggleLoading?: boolean;
    onMvToggle?: (enable: boolean) => void;
    onOpenMvSettings?: () => void;
    isMomentumEmailSettings?: boolean;
    momentumEmailConnected?: boolean;
    momentumEmailError?: string | null;
    onOpenMomentumEmailSettings?: () => void;
    isSmsToggle?: boolean;
    smsEnabled?: boolean;
    smsToggleLoading?: boolean;
    onSmsToggle?: (enable: boolean) => void;
    onOpenSmsActivity?: () => void;
}
const GICON = (product: string, label: string) => (<img width='28' height='28' src={`https://ssl.gstatic.com/images/branding/product/2x/${product}_2020q4_48dp.png`} alt={label}/>);
const APP_ICONS: Record<string, React.ReactNode> = {
    gmail: GICON('gmail', 'Gmail'),
    momentum_email_exchange: GICON('gmail', 'Momentum email exchange - Gmail'),
    google_calendar: GICON('calendar', 'Google Calendar'),
    google_drive: GICON('drive', 'Google Drive'),
    google_sheets: GICON('sheets', 'Google Sheets'),
    outlook: (<img width='28' height='28' src='https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://outlook.com&size=128' alt='Outlook 365'/>),
    outlook_calendar: (<img width='28' height='28' src='https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://outlook.com&size=128' alt='Outlook Calendar'/>),
    microsoft_teams: (<img width='28' height='28' src='https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://teams.microsoft.com&size=128' alt='Microsoft Teams'/>),
    slack: (<img width='28' height='28' src='https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://slack.com&size=128' alt='Slack'/>),
    hubspot: (<img width='28' height='28' src='https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://hubspot.com&size=128' alt='HubSpot'/>),
    jotform: (<img width='28' height='28' src='https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://jotform.com&size=128' alt='JotForms'/>),
    docusign: (<img width='28' height='28' src='https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://docusign.com&size=128' alt='DocuSign'/>),
    lightspeed_voice: (<img width='28' height='28' src='https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://lightspeedvoice.com&size=128' alt='Lightspeed Voice'/>),
    gte_voice: (<img width='28' height='28' src='https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://phones.gtphones.com&size=128' alt='GTE Voice'/>),
    momentum_voice: (<img width='28' height='28' src={`${process.env.PUBLIC_URL}/favicon.ico`} alt='Momentum Voice'/>),
    momentum_sms: (<img width='28' height='28' src={`${process.env.PUBLIC_URL}/favicon.ico`} alt='Momentum SMS'/>),
};
function getAppIcon(appId: string): React.ReactNode {
    return APP_ICONS[appId] ?? null;
}
const AppCard: React.FC<AppCardProps> = ({ app, connectedApp, onConnect, onDisconnect, onConfirmDisconnect, onCancelDisconnect, onToggleTrigger, onOpenOAuthSetup, onOpenLsvSetup, isConnecting, isDisconnecting, isConfirming, keyMissing, needsOAuthSetup, linkedAppName, linkedDependents, isLsvSetup, lsvConnected, triggerEnabled, triggerLoading, isGteToggle, gteEnabled, gteToggleLoading, onGteToggle, isMvSettings, mvEnabled, mvToggleLoading, onMvToggle, onOpenMvSettings, isMomentumEmailSettings, momentumEmailConnected, momentumEmailError, onOpenMomentumEmailSettings, isSmsToggle, smsEnabled, smsToggleLoading, onSmsToggle, onOpenSmsActivity, }) => {
    const isComingSoon = app.coming_soon === true;
    const status = connectedApp?.status;
    const isConnected = isMomentumEmailSettings
        ? !!momentumEmailConnected
        : !isComingSoon && status === 'active';
    const isPending = !isComingSoon && status === 'pending';
    const isReconnectable = !isComingSoon &&
        !!connectedApp &&
        status !== undefined &&
        status !== 'active' &&
        status !== 'pending';
    const isFailed = isReconnectable && status === 'failed';
    const isExpired = isReconnectable && status === 'expired';
    const isInitiated = isReconnectable && status === 'initiated';
    const isDisconnected = isReconnectable && status === 'disconnected';
    const isLoading = isConnecting || isDisconnecting;
    const formatDate = (dateStr: string | null) => {
        if (!dateStr)
            return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };
    return (<motion.div className={`integration-card ${isConnected ? 'integration-card--connected' : ''}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
      <div className='integration-card__header'>
        <div className='integration-card__icon'>
          {getAppIcon(app.id) ?? app.icon}
        </div>
        <div className='integration-card__info'>
          <h3 className='integration-card__name'>{app.name}</h3>
          <span className='integration-card__category'>{app.category}</span>
        </div>
        {(isConnected || (isLsvSetup && lsvConnected)) && (<div className='integration-card__badge integration-card__badge--connected'>
            <FiCheck size={12}/>
            Connected
          </div>)}
        {isPending && (<div className='integration-card__badge integration-card__badge--pending'>
            <FiRefreshCw size={12} className='spin'/>
            Pending
          </div>)}
        {isFailed && (<div className='integration-card__badge integration-card__badge--failed'>
            <FiAlertTriangle size={12}/>
            Failed
          </div>)}
        {isExpired && (<div className='integration-card__badge integration-card__badge--failed'>
            <FiAlertTriangle size={12}/>
            Expired — Reconnect
          </div>)}
        {isInitiated && (<div className='integration-card__badge integration-card__badge--failed'>
            <FiAlertTriangle size={12}/>
            Incomplete — Reconnect
          </div>)}
        {isDisconnected && (<div className='integration-card__badge integration-card__badge--failed'>
            <FiAlertTriangle size={12}/>
            Disconnected — Reconnect
          </div>)}
        {isComingSoon && !isConnected && (<div className='integration-card__badge integration-card__badge--coming-soon'>
            Coming Soon
          </div>)}
      </div>

      <p className='integration-card__description'>{app.description}</p>

      {linkedAppName && !isConnected && (<p className='integration-card__linked-hint'>
          <FiLink size={12}/>
          <span>Uses the {linkedAppName} connection: one sign-in enables both.</span>
        </p>)}

      
      {isConnected &&
            (app.id === 'gmail' || app.id === 'outlook') &&
            onToggleTrigger && (<div className='integration-card__trigger'>
            <div className='integration-card__trigger-info'>
              <FiBell size={13}/>
              <span>New email trigger</span>
            </div>
            <button className={`integration-trigger-toggle ${triggerEnabled ? 'integration-trigger-toggle--on' : ''}`} onClick={() => onToggleTrigger(app.id, !triggerEnabled)} disabled={triggerLoading} title={triggerEnabled
                ? 'Disable new email trigger'
                : 'Enable new email trigger'}>
              {triggerLoading ? (<FiRefreshCw size={12} className='spin'/>) : triggerEnabled ? (<>
                  <span className='integration-trigger-toggle__dot'/> On
                </>) : (<>
                  <span className='integration-trigger-toggle__dot'/> Off
                </>)}
            </button>
          </div>)}

      
      {isGteToggle && onGteToggle && (<div className='integration-card__trigger'>
          <div className='integration-card__trigger-info'>
            <FiBell size={13}/>
            <span>Enable GTE Voice</span>
          </div>
          <button className={`integration-trigger-toggle ${gteEnabled ? 'integration-trigger-toggle--on' : ''}`} onClick={() => onGteToggle(!gteEnabled)} disabled={gteToggleLoading} title={gteEnabled ? 'Disable GTE Voice' : 'Enable GTE Voice'}>
            {gteToggleLoading ? (<FiRefreshCw size={12} className='spin'/>) : gteEnabled ? (<>
                <span className='integration-trigger-toggle__dot'/> On
              </>) : (<>
                <span className='integration-trigger-toggle__dot'/> Off
              </>)}
          </button>
        </div>)}

      
      {isMvSettings && onMvToggle && (<div className='integration-card__trigger'>
          <div className='integration-card__trigger-info'>
            <FiBell size={13}/>
            <span>Enable integration</span>
          </div>
          <button className={`integration-trigger-toggle ${mvEnabled ? 'integration-trigger-toggle--on' : ''}`} onClick={() => onMvToggle(!mvEnabled)} disabled={mvToggleLoading} title={mvEnabled ? 'Disable integration' : 'Enable integration'}>
            {mvToggleLoading ? (<FiRefreshCw size={12} className='spin'/>) : mvEnabled ? (<>
                <span className='integration-trigger-toggle__dot'/> On
              </>) : (<>
                <span className='integration-trigger-toggle__dot'/> Off
              </>)}
          </button>
        </div>)}

      {isSmsToggle && onSmsToggle && (<div className='integration-card__trigger'>
          <div className='integration-card__trigger-info'>
            <FiBell size={13}/>
            <span>Enable integration</span>
          </div>
          <button className={`integration-trigger-toggle ${smsEnabled ? 'integration-trigger-toggle--on' : ''}`} onClick={() => onSmsToggle(!smsEnabled)} disabled={smsToggleLoading} title={smsEnabled ? 'Disable integration' : 'Enable integration'}>
            {smsToggleLoading ? (<FiRefreshCw size={12} className='spin'/>) : smsEnabled ? (<>
                <span className='integration-trigger-toggle__dot'/> On
              </>) : (<>
                <span className='integration-trigger-toggle__dot'/> Off
              </>)}
          </button>
        </div>)}

      {isSmsToggle && onOpenSmsActivity && (<div className='integration-card__actions'>
          <button className='integration-btn integration-btn--connect' onClick={onOpenSmsActivity} title='See what happened to each text message'>
            <FiActivity size={14}/>
            Activity
          </button>
        </div>)}

      {isConnected && connectedApp && (<div className='integration-card__meta'>
          {connectedApp.connected_at && (<span className='integration-card__date'>
              Connected {formatDate(connectedApp.connected_at)}
            </span>)}
          {connectedApp.last_used_at && (<span className='integration-card__date'>
              Last used {formatDate(connectedApp.last_used_at)}
            </span>)}
        </div>)}

      
      {isMvSettings && onOpenMvSettings && (<div className='integration-card__actions'>
          <button className='integration-btn integration-btn--connect' onClick={onOpenMvSettings} disabled={!mvEnabled} title={mvEnabled
                ? 'Select voice agents to integrate'
                : 'Enable Momentum Voice first'}>
            <FiSettings size={14}/>
            Settings
          </button>
        </div>)}

      
      {isMomentumEmailSettings && momentumEmailError && (<div className='integration-card__error' role='alert'>
          <FiAlertTriangle size={13}/>
          <span>{momentumEmailError}</span>
        </div>)}

      {isMomentumEmailSettings && onOpenMomentumEmailSettings && (<div className='integration-card__actions'>
          <button className='integration-btn integration-btn--connect' onClick={onOpenMomentumEmailSettings} disabled={!isConnected} title={isConnected
                ? 'View and manage connected email integrations'
                : 'No email integration connected'}>
            <FiSettings size={14}/>
            Settings
          </button>
        </div>)}

      
      {!isGteToggle && !isMvSettings && !isMomentumEmailSettings && !isSmsToggle && (<div className='integration-card__actions'>
          {isLsvSetup ? (<button className='integration-btn integration-btn--connect' onClick={onOpenLsvSetup} disabled={isLoading} title='Configure Lightspeed Voice credentials'>
              <FiLock size={14}/>
              {lsvConnected ? 'Manage' : 'Set Up'}
            </button>) : isConnected && linkedAppName ? (<span className='integration-card__linked-note' title={`Connected through your ${linkedAppName} connection. Disconnect ${linkedAppName} to remove it.`}>
              <FiLink size={13}/>
              Uses your {linkedAppName} connection
            </span>) : isConnected ? (isConfirming ? (<div className='integration-card__confirm'>
                <span className='integration-card__confirm-label'>
                  {linkedDependents ? `Disconnect? (also removes ${linkedDependents})` : 'Disconnect?'}
                </span>
                <button className='integration-btn integration-btn--confirm-yes' onClick={() => onConfirmDisconnect(app.id)} disabled={isDisconnecting}>
                  {isDisconnecting ? (<>
                      <FiRefreshCw size={14} className='spin'/>{' '}
                      Disconnecting...
                    </>) : (<>
                      <FiCheck size={14}/> Yes
                    </>)}
                </button>
                <button className='integration-btn integration-btn--confirm-no' onClick={onCancelDisconnect} disabled={isDisconnecting}>
                  <FiX size={14}/> No
                </button>
              </div>) : (<button className='integration-btn integration-btn--disconnect' onClick={() => onDisconnect(app.id)} disabled={isLoading}>
                <FiX size={14}/>
                Disconnect
              </button>)) : isComingSoon ? (<button className='integration-btn integration-btn--coming-soon' disabled={true}>
              Coming Soon
            </button>) : keyMissing ? (<button className='integration-btn integration-btn--connect integration-btn--locked' disabled={true} title='Add your Composio API key below to enable connections'>
              <FiLock size={14}/>
              Connect
            </button>) : needsOAuthSetup ? (<button className='integration-btn integration-btn--connect' onClick={() => onOpenOAuthSetup?.(app.id)} disabled={isLoading} title={`Configure your own ${app.name} OAuth client (used by your account only)`}>
              <FiLock size={14}/>
              Set Up
            </button>) : (<button className='integration-btn integration-btn--connect' onClick={() => onConnect(app.id)} disabled={isLoading}>
              {isConnecting ? (<>
                  <FiRefreshCw size={14} className='spin'/>
                  Connecting...
                </>) : isReconnectable ? (<>
                  <FiRefreshCw size={14}/>
                  Reconnect
                </>) : (<>
                  <FiZap size={14}/>
                  Connect
                </>)}
            </button>)}
        </div>)}
    </motion.div>);
};
function formatRelativeTime(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1)
        return 'Just now';
    if (mins < 60)
        return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24)
        return `${hours}h ago`;
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    });
}
const IntegrationsPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [availableApps, setAvailableApps] = useState<AvailableApp[]>(AVAILABLE_APPS.filter(isAppVisible));
    const [connectedApps, setConnectedApps] = useState<ConnectedApp[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [setupRequired, setSetupRequired] = useState(false);
    const [connectingApp, setConnectingApp] = useState<string | null>(null);
    const [disconnectingApp, setDisconnectingApp] = useState<string | null>(null);
    const [confirmingDisconnect, setConfirmingDisconnect] = useState<string | null>(null);
    const [toastMessage, setToastMessage] = useState<{
        type: 'success' | 'error';
        message: string;
    } | null>(null);
    const [creatingProject, setCreatingProject] = useState(false);
    const [keyConfigured, setKeyConfigured] = useState(false);
    const [triggerStates, setTriggerStates] = useState<Record<EmailTriggerProvider, boolean>>({ gmail: false, outlook: false });
    const [triggerLoadingProvider, setTriggerLoadingProvider] = useState<EmailTriggerProvider | null>(null);
    const anyTriggerEnabled = triggerStates.gmail || triggerStates.outlook;
    const [triggerEvents, setTriggerEvents] = useState<TriggerEvent[]>([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [oauthAppsConfigured, setOauthAppsConfigured] = useState<Set<string>>(new Set());
    const [oauthSetupAppId, setOauthSetupAppId] = useState<string | null>(null);
    const [oauthSetupClientId, setOauthSetupClientId] = useState('');
    const [oauthSetupClientSecret, setOauthSetupClientSecret] = useState('');
    const [oauthSetupSaving, setOauthSetupSaving] = useState(false);
    const [oauthSetupError, setOauthSetupError] = useState<string | null>(null);
    const [oauthRedirectCopiedIndex, setOauthRedirectCopiedIndex] = useState<number | null>(null);
    const [lsvModalOpen, setLsvModalOpen] = useState(false);
    const [lsvLoading, setLsvLoading] = useState(false);
    const [lsvSaving, setLsvSaving] = useState(false);
    const [lsvRemoving, setLsvRemoving] = useState(false);
    const [lsvEmail, setLsvEmail] = useState('');
    const [lsvToken, setLsvToken] = useState('');
    const [lsvApiKey, setLsvApiKey] = useState('');
    const [lsvShowToken, setLsvShowToken] = useState(false);
    const [lsvConnected, setLsvConnected] = useState(false);
    const [lsvEmailError, setLsvEmailError] = useState('');
    const [lsvTokenError, setLsvTokenError] = useState('');
    const [lsvWebhookCopied, setLsvWebhookCopied] = useState(false);
    const [lsvError, setLsvError] = useState<string | null>(null);
    const LIGHTSPEED_WEBHOOK_URL = `${process.env.REACT_APP_MyAMP_Url}/api/lightspeed-voice/webhook`;
    const [gteEnabled, setGteEnabled] = useState(false);
    const [gteToggleLoading, setGteToggleLoading] = useState(false);
    const [mvEnabled, setMvEnabled] = useState(false);
    const [mvToggleLoading, setMvToggleLoading] = useState(false);
    const [smsEnabled, setSmsEnabled] = useState(false);
    const [smsToggleLoading, setSmsToggleLoading] = useState(false);
    const [mvModalOpen, setMvModalOpen] = useState(false);
    const [mvLoading, setMvLoading] = useState(false);
    const [mvSavingAgentId, setMvSavingAgentId] = useState<string | null>(null);
    const [mvAgents, setMvAgents] = useState<{
        id: string;
        name: string;
        enabled: boolean;
    }[]>([]);
    const [mvError, setMvError] = useState<string | null>(null);
    const [momentumEmailConnected, setMomentumEmailConnected] = useState(false);
    const [momentumEmailModalOpen, setMomentumEmailModalOpen] = useState(false);
    // Which integration's activity log is open, or null. Opened from that
    // integration's own settings modal, so it is always scoped to one source.
    const [activityModalSource, setActivityModalSource] = useState<ActivitySource | null>(null);
    const [momentumEmailLoading, setMomentumEmailLoading] = useState(false);
    const [momentumEmailIntegrations, setMomentumEmailIntegrations] = useState<MacEmailIntegration[]>([]);
    const [momentumEmailSavingId, setMomentumEmailSavingId] = useState<number | null>(null);
    const [momentumEmailShowUnsupported, setMomentumEmailShowUnsupported] = useState(false);
    const [momentumEmailError, setMomentumEmailError] = useState<string | null>(null);
    const [momentumEmailNotConfigured, setMomentumEmailNotConfigured] = useState(false);
    const loadConnectedApps = useCallback(async () => {
        try {
            const apps = await composioClient.getConnectedApps();
            setConnectedApps(apps);
        }
        catch (err) {
            console.error('Failed to load connected apps:', err);
        }
    }, []);
    const checkSetupStatus = useCallback(async () => {
        try {
            const status = await composioClient.checkStatus();
            const noKey = status.setup_required === true || status.has_key === false;
            setSetupRequired(noKey);
            setKeyConfigured(!noKey);
        }
        catch (err) {
            console.error('Failed to check Composio setup status:', err);
        }
    }, []);
    const loadAvailableApps = useCallback(async () => {
        try {
            const apps = await composioClient.getAvailableApps();
            setAvailableApps(apps);
        }
        catch (err) {
            console.error('Failed to load available apps:', err);
            setAvailableApps(AVAILABLE_APPS.filter(isAppVisible));
        }
    }, []);
    const loadTriggerStatus = useCallback(async () => {
        try {
            const status = await getEmailTriggerStatus();
            if (status.providers) {
                setTriggerStates({
                    gmail: status.providers.gmail?.enabled ?? false,
                    outlook: status.providers.outlook?.enabled ?? false,
                });
            }
            else if (status.provider === 'outlook' || status.provider === 'gmail') {
                const detected = status.provider;
                setTriggerStates({
                    gmail: detected === 'gmail' && status.enabled,
                    outlook: detected === 'outlook' && status.enabled,
                });
            }
        }
        catch {
        }
    }, []);
    const loadGteStatus = useCallback(async () => {
        try {
            const status = await getGteVoiceStatus();
            setGteEnabled(status.isEnabled);
        }
        catch {
        }
    }, []);
    const loadMvStatus = useCallback(async () => {
        try {
            const status = await getMomentumVoiceStatus();
            setMvEnabled(status.isEnabled);
        }
        catch {
        }
    }, []);
    const loadSmsStatus = useCallback(async () => {
        try {
            const status = await getMomentumSmsStatus();
            setSmsEnabled(status.isEnabled);
        }
        catch {
        }
    }, []);
    const loadMomentumEmailStatus = useCallback(async () => {
        try {
            // No provider filter: Momentum email exchange covers Gmail, Exchange and IMAP, so the
            // "connected" state must reflect any connected provider — not Gmail only. Hardcoding
            // 'gmail' hid agencies whose integrations are Exchange/IMAP.
            const { hasIntegration } = await checkEmailIntegration();
            setMomentumEmailConnected(hasIntegration);
            setMomentumEmailNotConfigured(false);
            setMomentumEmailError(null);
        }
        catch (err) {
            if (err instanceof EmailExchangeNotConfiguredError) {
                setMomentumEmailConnected(false);
                setMomentumEmailNotConfigured(true);
                setMomentumEmailError(null);
                return;
            }
            // Any other failure (e.g. a 500 "Failed to resolve email secret key") was previously
            // swallowed here, so the card silently read as "not connected" with no clue why.
            // Surface it inline on the card instead — quiet (no toast on every page load) but visible.
            setMomentumEmailConnected(false);
            setMomentumEmailError(err instanceof Error ? err.message : 'Failed to check email integration');
        }
    }, []);
    const loadMvAgents = useCallback(async () => {
        setMvLoading(true);
        setMvError(null);
        try {
            const [agents, states] = await Promise.all([
                getMomentumVoiceAgents(),
                getMomentumVoiceAgentStates(),
            ]);
            const enabledMap = new Map(states.map((s) => [s.ElevenLabsAgentId, s.IsEnabled]));
            const merged = agents
                .filter((a) => !!a.ElevenLabsAgentId)
                .map((a) => ({
                id: a.ElevenLabsAgentId,
                name: a.AgentName || a.ElevenLabsAgentId,
                enabled: enabledMap.get(a.ElevenLabsAgentId) ?? false,
            }));
            setMvAgents(merged);
        }
        catch (err) {
            setMvError(err instanceof Error ? err.message : 'Failed to load voice agents');
        }
        finally {
            setMvLoading(false);
        }
    }, []);
    const loadOAuthAppsConfigured = useCallback(async () => {
        try {
            const apps = await listOAuthApps();
            setOauthAppsConfigured(new Set(apps.filter((a) => a.is_active).map((a) => a.toolkit.toLowerCase())));
        }
        catch {
        }
    }, []);
    const loadTriggerEvents = useCallback(async (showSpinner = false) => {
        if (showSpinner)
            setEventsLoading(true);
        try {
            const events = await getTriggerEvents(15);
            setTriggerEvents(events);
        }
        catch {
        }
        finally {
            if (showSpinner)
                setEventsLoading(false);
        }
    }, []);
    useEffect(() => {
        if (!anyTriggerEnabled)
            return;
        loadTriggerEvents(true);
        const unsub = subscribeTriggerEvents((event) => {
            setTriggerEvents((prev) => prev.some((e) => e.id === event.id) ? prev : [event, ...prev]);
        });
        return unsub;
    }, [anyTriggerEnabled, loadTriggerEvents]);
    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await Promise.all([
                loadConnectedApps(),
                loadAvailableApps(),
                checkSetupStatus(),
                loadTriggerStatus(),
                loadOAuthAppsConfigured(),
                loadMvStatus(),
                loadSmsStatus(),
                loadMomentumEmailStatus(),
            ]);
        }
        catch (err) {
            setError('Failed to load integrations. Please try again.');
        }
        finally {
            setLoading(false);
        }
    }, [
        loadConnectedApps,
        loadAvailableApps,
        checkSetupStatus,
        loadTriggerStatus,
        loadOAuthAppsConfigured,
        loadMvStatus,
        loadSmsStatus,
        loadMomentumEmailStatus,
    ]);
    useEffect(() => {
        loadData();
    }, [loadData]);
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const oauthStatus = params.get('oauth_status');
        if (oauthStatus === 'success') {
            showToast('success', 'App connected successfully!');
            loadConnectedApps();
            navigate(location.pathname, { replace: true });
        }
        else if (oauthStatus === 'error') {
            const errorMessage = params.get('message') || 'Failed to connect app';
            showToast('error', errorMessage);
            navigate(location.pathname, { replace: true });
        }
    }, [location, navigate, loadConnectedApps]);
    const showToast = (type: 'success' | 'error', message: string) => {
        setToastMessage({ type, message });
        setTimeout(() => setToastMessage(null), 5000);
    };
    const handleRequestAccess = async () => {
        setCreatingProject(true);
        try {
            await createProject();
            showToast('success', 'Access granted — you can now connect your apps.');
            setSetupRequired(false);
            setKeyConfigured(true);
        }
        catch (err) {
            showToast('error', err instanceof Error
                ? err.message
                : 'Failed to create Composio project');
        }
        finally {
            setCreatingProject(false);
        }
    };
    const handleToggleTrigger = async (appId: string, enable: boolean) => {
        const provider: EmailTriggerProvider = appId === 'outlook' ? 'outlook' : 'gmail';
        setTriggerLoadingProvider(provider);
        try {
            if (enable) {
                await enableEmailTrigger(provider);
                setTriggerStates((prev) => ({ ...prev, [provider]: true }));
                showToast('success', 'New email trigger enabled — AI will be notified of incoming emails');
            }
            else {
                await disableEmailTrigger(provider);
                setTriggerStates((prev) => ({ ...prev, [provider]: false }));
                showToast('success', 'New email trigger disabled');
            }
        }
        catch (err) {
            showToast('error', err instanceof Error ? err.message : 'Failed to update trigger');
        }
        finally {
            setTriggerLoadingProvider(null);
        }
    };
    const pollUntilActive = useCallback(async (appId: string) => {
        const MAX_ATTEMPTS = 8;
        const INTERVAL_MS = 2000;
        for (let i = 0; i < MAX_ATTEMPTS; i++) {
            await new Promise<void>((r) => setTimeout(r, INTERVAL_MS));
            try {
                const apps = await composioClient.getConnectedApps();
                setConnectedApps(apps);
                const target = apps.find((a) => a.app.toLowerCase() === appId.toLowerCase());
                if (target?.status === 'active')
                    break;
            }
            catch {
            }
        }
        setConnectingApp(null);
    }, []);
    const markAppActiveLocally = useCallback((appId: string) => {
        const lower = appId.toLowerCase();
        // A linked tile (Outlook Calendar) and the app whose connection it uses
        // (Outlook 365) come out of the same OAuth flow, so both light up.
        const self = availableApps.find((a) => a.id.toLowerCase() === lower);
        const ids = [lower];
        for (const a of availableApps) {
            const id = a.id.toLowerCase();
            const isParent = !!self?.linked_app && id === self.linked_app.toLowerCase();
            const isChild = a.linked_app?.toLowerCase() === lower;
            if ((isParent || isChild) && !ids.includes(id))
                ids.push(id);
        }
        setConnectedApps((prev) => {
            const nowIso = new Date().toISOString();
            const next = [...prev];
            for (const id of ids) {
                const cfg = availableApps.find((a) => a.id.toLowerCase() === id);
                const idx = next.findIndex((a) => a.app.toLowerCase() === id);
                if (idx >= 0) {
                    next[idx] = {
                        ...next[idx],
                        status: 'active',
                        connected_at: next[idx].connected_at ?? nowIso,
                    };
                    continue;
                }
                next.push({
                    app: id,
                    app_display_name: cfg?.name ?? id,
                    icon: cfg?.icon ?? '🔗',
                    status: 'active',
                    connection_id: null,
                    connected_at: nowIso,
                    last_used_at: null,
                    ...(cfg?.linked_app ? { linked_app: cfg.linked_app } : {}),
                });
            }
            return next;
        });
    }, [availableApps]);
    const handleConnect = async (appId: string) => {
        if (setupRequired)
            return;
        setConnectingApp(appId);
        const wasPreviouslyConnected = connectedApps.some((a) => a.app.toLowerCase() === appId.toLowerCase() && a.status === 'active');
        const popupWidth = 600;
        const popupHeight = 700;
        const left = window.screenX + (window.outerWidth - popupWidth) / 2;
        const top = window.screenY + (window.outerHeight - popupHeight) / 2;
        const oauthPopup = window.open('about:blank', 'composio-oauth', `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes`);
        if (!oauthPopup) {
            showToast('error', 'Popup was blocked. Please allow popups for this site and try again.');
            setConnectingApp(null);
            return;
        }
        let poll: ReturnType<typeof setInterval>;
        let msgHandler: (e: MessageEvent) => void;
        let checkPending = false;
        let done = false;
        const closePopup = () => {
            try {
                if (oauthPopup && !oauthPopup.closed)
                    oauthPopup.close();
            }
            catch {
            }
        };
        const onConnectSuccess = () => {
            markAppActiveLocally(appId);
            setConnectingApp(null);
            showToast('success', 'App connected successfully!');
            if (appId.toLowerCase() === 'gmail' && !wasPreviouslyConnected) {
                setTriggerStates((prev) => ({ ...prev, gmail: false }));
                disableEmailTrigger('gmail').catch(() => {
                });
            }
            void pollUntilActive(appId);
        };
        const finish = (success: boolean, errorMsg?: string) => {
            if (done)
                return;
            done = true;
            clearInterval(poll);
            window.removeEventListener('focus', checkConnection);
            document.removeEventListener('click', checkConnection);
            window.removeEventListener('message', msgHandler);
            closePopup();
            if (success) {
                onConnectSuccess();
                return;
            }
            if (errorMsg) {
                showToast('error', errorMsg);
                void loadConnectedApps();
                setConnectingApp(null);
                return;
            }
            void (async () => {
                try {
                    const apps = await composioClient.getConnectedApps();
                    setConnectedApps(apps);
                    const target = apps.find((a) => a.app.toLowerCase() === appId.toLowerCase());
                    if (target?.status === 'active') {
                        done = false;
                        onConnectSuccess();
                        return;
                    }
                }
                catch {
                }
                void pollUntilActive(appId);
            })();
        };
        async function checkConnection() {
            if (done || checkPending || oauthPopup?.closed)
                return;
            checkPending = true;
            try {
                const apps = await composioClient.getConnectedApps();
                const target = apps.find((a) => a.app.toLowerCase() === appId.toLowerCase());
                if (target?.status === 'active') {
                    finish(true);
                }
            }
            catch {
            }
            checkPending = false;
        }
        msgHandler = (e: MessageEvent) => {
            if (e.origin !== window.location.origin)
                return;
            if (e.data?.type !== 'composio-oauth-complete')
                return;
            if (e.data.status === 'success')
                finish(true);
            else
                finish(false, e.data.error || 'Failed to connect app');
        };
        window.addEventListener('message', msgHandler);
        poll = setInterval(() => {
            if (oauthPopup.closed)
                finish(false, undefined);
        }, 500);
        window.addEventListener('focus', checkConnection);
        document.addEventListener('click', checkConnection);
        setTimeout(() => {
            if (!oauthPopup.closed)
                oauthPopup.close();
            finish(false, undefined);
        }, 5 * 60 * 1000);
        try {
            const authUrl = await composioClient.connectApp(appId);
            oauthPopup.location.href = authUrl;
        }
        catch (err) {
            closePopup();
            clearInterval(poll);
            window.removeEventListener('focus', checkConnection);
            document.removeEventListener('click', checkConnection);
            window.removeEventListener('message', msgHandler);
            setConnectingApp(null);
            if (isOAuthSetupRequiredError(err)) {
                // Composio has no managed OAuth app for this toolkit (Google Chat,
                // DocuSign) and this user has not saved their own OAuth client
                // yet. Open the setup dialog so they can paste their client ID
                // and secret (stored for their account only, like their own
                // Gmail connection); afterwards Connect behaves exactly like Gmail.
                const toolkit = (err.toolkit || appId).toLowerCase();
                const name = availableApps.find((a) => a.id.toLowerCase() === toolkit)?.name ?? getOAuthSetupGuide(toolkit).displayName;
                showToast('error', `${name} needs your own OAuth client before you can connect.`);
                openOAuthSetup(toolkit);
                return;
            }
            showToast('error', err instanceof Error ? err.message : 'Failed to connect app');
        }
    };
    const openLsvModal = useCallback(async () => {
        setLsvModalOpen(true);
        setLsvLoading(true);
        setLsvError(null);
        setLsvEmailError('');
        setLsvTokenError('');
        try {
            const settings = await getLightspeedSettings();
            const connected = !!settings?.lsvEmail;
            setLsvConnected(connected);
            setLsvEmail(settings?.lsvEmail ?? '');
            setLsvToken(settings?.lsvToken ?? '');
            setLsvApiKey(settings?.apiKey ?? '');
        }
        catch (err) {
            setLsvError(err instanceof Error ? err.message : 'Failed to load settings');
        }
        finally {
            setLsvLoading(false);
        }
    }, []);
    const closeLsvModal = useCallback(() => {
        if (lsvSaving || lsvRemoving)
            return;
        setLsvModalOpen(false);
        setLsvEmail('');
        setLsvToken('');
        setLsvApiKey('');
        setLsvEmailError('');
        setLsvTokenError('');
        setLsvError(null);
        setLsvShowToken(false);
    }, [lsvSaving, lsvRemoving]);
    const validateLsv = () => {
        let valid = true;
        setLsvEmailError('');
        setLsvTokenError('');
        if (!lsvEmail.trim()) {
            setLsvEmailError('Please enter your Lightspeed Voice email');
            valid = false;
        }
        if (!lsvToken.trim() || lsvToken === '••••••••••••') {
            setLsvTokenError('Please enter your Lightspeed Voice token');
            valid = false;
        }
        return valid;
    };
    const handleLsvSave = async () => {
        if (!validateLsv())
            return;
        setLsvSaving(true);
        setLsvError(null);
        try {
            await saveLightspeedSettings({
                lsvEmail,
                lsvToken: lsvToken === '••••••••••••' ? '' : lsvToken,
                apiKey: lsvApiKey === '••••••••••••' ? '' : lsvApiKey,
            });
            showToast('success', 'Lightspeed Voice settings saved successfully');
            setLsvConnected(true);
            closeLsvModal();
        }
        catch (err) {
            setLsvError(err instanceof Error ? err.message : 'Failed to save settings');
        }
        finally {
            setLsvSaving(false);
        }
    };
    const handleLsvSaveApiKey = async () => {
        if (!lsvApiKey.trim() || lsvApiKey === '••••••••••••')
            return;
        setLsvSaving(true);
        setLsvError(null);
        try {
            await saveLightspeedSettings({
                lsvEmail,
                lsvToken: '',
                apiKey: lsvApiKey,
            });
            showToast('success', 'API Key saved successfully');
            closeLsvModal();
        }
        catch (err) {
            setLsvError(err instanceof Error ? err.message : 'Failed to save API Key');
        }
        finally {
            setLsvSaving(false);
        }
    };
    const handleLsvRemove = async () => {
        setLsvRemoving(true);
        setLsvError(null);
        try {
            await removeLightspeedSettings();
            showToast('success', 'Lightspeed Voice integration removed');
            setLsvConnected(false);
            closeLsvModal();
        }
        catch (err) {
            setLsvError(err instanceof Error ? err.message : 'Failed to remove integration');
        }
        finally {
            setLsvRemoving(false);
        }
    };
    const handleLsvCopyWebhook = () => {
        navigator.clipboard.writeText(LIGHTSPEED_WEBHOOK_URL).then(() => {
            setLsvWebhookCopied(true);
            setTimeout(() => setLsvWebhookCopied(false), 2500);
        });
    };
    const handleGteToggle = async (enable: boolean) => {
        setGteToggleLoading(true);
        try {
            if (enable) {
                await enableGteVoice();
                setGteEnabled(true);
                showToast('success', 'GTE Voice integration enabled');
            }
            else {
                await disableGteVoice();
                setGteEnabled(false);
                showToast('success', 'GTE Voice integration disabled');
            }
        }
        catch (err) {
            showToast('error', err instanceof Error
                ? err.message
                : 'Failed to update GTE Voice integration');
        }
        finally {
            setGteToggleLoading(false);
        }
    };
    const handleMvToggle = async (enable: boolean) => {
        setMvToggleLoading(true);
        try {
            if (enable) {
                await enableMomentumVoice();
                setMvEnabled(true);
                showToast('success', 'Momentum Voice enabled');
            }
            else {
                await disableMomentumVoice();
                setMvEnabled(false);
                showToast('success', 'Momentum Voice disabled');
            }
        }
        catch (err) {
            showToast('error', err instanceof Error
                ? err.message
                : 'Failed to update Momentum Voice integration');
        }
        finally {
            setMvToggleLoading(false);
        }
    };
    const handleSmsToggle = async (enable: boolean) => {
        setSmsToggleLoading(true);
        try {
            await setMomentumSmsState(enable);
            setSmsEnabled(enable);
            showToast('success', enable ? 'Momentum SMS enabled' : 'Momentum SMS disabled');
        }
        catch (err) {
            showToast('error', err instanceof Error
                ? err.message
                : 'Failed to update Momentum SMS integration');
        }
        finally {
            setSmsToggleLoading(false);
        }
    };
    const openMvModal = useCallback(() => {
        setMvModalOpen(true);
        void loadMvAgents();
    }, [loadMvAgents]);
    const closeMvModal = useCallback(() => {
        if (mvSavingAgentId)
            return;
        setMvModalOpen(false);
        setMvError(null);
    }, [mvSavingAgentId]);
    const handleMvAgentToggle = async (agentId: string, enable: boolean) => {
        setMvSavingAgentId(agentId);
        setMvAgents((prev) => prev.map((a) => (a.id === agentId ? { ...a, enabled: enable } : a)));
        try {
            await setMomentumVoiceAgentState(agentId, enable);
            showToast('success', enable ? 'Agent integration enabled' : 'Agent integration disabled');
        }
        catch (err) {
            setMvAgents((prev) => prev.map((a) => (a.id === agentId ? { ...a, enabled: !enable } : a)));
            showToast('error', err instanceof Error ? err.message : 'Failed to update agent');
        }
        finally {
            setMvSavingAgentId(null);
        }
    };
    const loadMomentumEmailIntegrations = useCallback(async () => {
        setMomentumEmailLoading(true);
        setMomentumEmailError(null);
        setMomentumEmailNotConfigured(false);
        try {
            // No provider filter: show every connected integration (Gmail, Exchange, IMAP). The
            // previous 'gmail' filter dropped Exchange/IMAP integrations, so an agency with only
            // those saw an empty list even though the API returned them.
            const list = await listEmailIntegrations();
            setMomentumEmailIntegrations(list);
        }
        catch (err) {
            if (err instanceof EmailExchangeNotConfiguredError) {
                setMomentumEmailNotConfigured(true);
                setMomentumEmailIntegrations([]);
            }
            else {
                setMomentumEmailError(err instanceof Error ? err.message : 'Failed to load integrations');
            }
        }
        finally {
            setMomentumEmailLoading(false);
        }
    }, []);
    const openMomentumEmailModal = useCallback(() => {
        setMomentumEmailModalOpen(true);
        setMomentumEmailShowUnsupported(false);
        void loadMomentumEmailIntegrations();
    }, [loadMomentumEmailIntegrations]);
    const closeMomentumEmailModal = useCallback(() => {
        if (momentumEmailSavingId !== null)
            return;
        setMomentumEmailModalOpen(false);
        setMomentumEmailError(null);
    }, [momentumEmailSavingId]);
    const isAiAutomationOn = (i: MacEmailIntegration): boolean => i.ai_automation_enabled ?? i.ai_automation ?? false;
    const supportedMomentumEmailIntegrations = momentumEmailIntegrations.filter(isAiSupportedIntegration);
    const unsupportedMomentumEmailIntegrations = momentumEmailIntegrations.filter((i) => !isAiSupportedIntegration(i));
    const handleMomentumEmailAiToggle = async (id: number, enable: boolean) => {
        const target = momentumEmailIntegrations.find((i) => i.id === id);
        if (enable && target && !isAiSupportedIntegration(target))
            return;
        setMomentumEmailSavingId(id);
        setMomentumEmailIntegrations((prev) => prev.map((i) => i.id === id
            ? { ...i, ai_automation_enabled: enable, ai_automation: enable }
            : i));
        try {
            await setEmailAiAutomation(id, enable);
            showToast('success', enable ? 'AI automation enabled' : 'AI automation disabled');
        }
        catch (err) {
            setMomentumEmailIntegrations((prev) => prev.map((i) => i.id === id
                ? { ...i, ai_automation_enabled: !enable, ai_automation: !enable }
                : i));
            showToast('error', err instanceof Error ? err.message : 'Failed to update AI automation');
        }
        finally {
            setMomentumEmailSavingId(null);
        }
    };
    const renderMomentumEmailRow = (intg: MacEmailIntegration) => {
        const supported = isAiSupportedIntegration(intg);
        const aiOn = isAiAutomationOn(intg);
        const purpose = String(intg.integration_purpose ?? '').replace(/_/g, ' ').trim();
        return (<div key={intg.id} className='integration-card__trigger' style={{ marginTop: 0, alignItems: 'flex-start', opacity: supported ? 1 : 0.75 }}>
            <div className='integration-card__trigger-info' style={{
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 3,
            }}>
              <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {intg.email_address}
                {intg.provider && (<span style={{
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                    padding: '1px 6px',
                    borderRadius: 4,
                    background: 'var(--surface-2, rgba(127,127,127,0.14))',
                    color: 'var(--text-secondary)',
                }}>
                    {intg.provider}
                  </span>)}
              </span>
              <span style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
            }}>
                <span style={{ textTransform: 'capitalize' }}>
                  {intg.status}
                </span>
                {!supported && purpose && (<span style={{ textTransform: 'capitalize' }}>· {purpose}</span>)}
                {intg.default && <span>· Default</span>}
                <span>· {intg.is_owner ? 'Owner' : 'Shared'}</span>
                {intg.last_used_at && (<span>
                    · Last used{' '}
                    {new Date(intg.last_used_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                })}
                  </span>)}
              </span>
            </div>
            <button className={`integration-trigger-toggle ${aiOn ? 'integration-trigger-toggle--on' : ''}`} onClick={() => handleMomentumEmailAiToggle(intg.id, !aiOn)} disabled={momentumEmailSavingId === intg.id || (!supported && !aiOn)} title={!supported
                ? 'AI automation works only with the Momentum MAC default email integration'
                : aiOn
                    ? 'Disable AI automation'
                    : 'Enable AI automation'}>
              {momentumEmailSavingId === intg.id ? (<FiRefreshCw size={12} className='spin'/>) : aiOn ? (<>
                  <span className='integration-trigger-toggle__dot'/>{' '}
                  On
                </>) : (<>
                  <span className='integration-trigger-toggle__dot'/>{' '}
                  Off
                </>)}
            </button>
          </div>);
    };
    const openOAuthSetup = (appId: string) => {
        setOauthSetupAppId(appId);
        setOauthSetupClientId('');
        setOauthSetupClientSecret('');
        setOauthSetupError(null);
    };
    const closeOAuthSetup = () => {
        if (oauthSetupSaving)
            return;
        setOauthSetupAppId(null);
        setOauthSetupClientId('');
        setOauthSetupClientSecret('');
        setOauthSetupError(null);
    };
    // Wording for the OAuth "Set up" modal (per toolkit, generic fallback).
    const oauthSetupGuide = getOAuthSetupGuide(oauthSetupAppId ?? '');
    const oauthSetupDisplayName = oauthSetupAppId
        ? (availableApps.find((a) => a.id.toLowerCase() === oauthSetupAppId.toLowerCase())?.name ?? oauthSetupGuide.displayName ?? oauthSetupAppId)
        : '';
    const handleCopyOAuthRedirect = (url: string, index: number) => {
        // navigator.clipboard is undefined on plain http; the URL stays visible to copy by hand.
        if (!navigator.clipboard)
            return;
        navigator.clipboard.writeText(url).then(() => {
            setOauthRedirectCopiedIndex(index);
            setTimeout(() => setOauthRedirectCopiedIndex(null), 2500);
        }).catch(() => {
            /* clipboard permission denied; nothing to do */
        });
    };
    const handleSaveOAuthSetup = async () => {
        if (!oauthSetupAppId)
            return;
        const rawClientId = oauthSetupClientId.trim();
        const clientSecret = oauthSetupClientSecret.trim();
        if (!rawClientId || !clientSecret) {
            setOauthSetupError(`Both ${oauthSetupGuide.clientIdLabel} and ${oauthSetupGuide.clientSecretLabel} are required.`);
            return;
        }
        // Browser credential autofill can glue the login email in front of the
        // pasted ID; strip it and reject values that are not a client ID at all.
        const normalized = normalizeOAuthClientId(oauthSetupAppId, rawClientId);
        if (normalized.error) {
            setOauthSetupError(normalized.error);
            return;
        }
        const secretProblem = validateOAuthClientSecret(oauthSetupAppId, clientSecret, oauthSetupGuide.clientSecretLabel);
        if (secretProblem) {
            setOauthSetupError(secretProblem);
            return;
        }
        const clientId = normalized.clientId;
        if (normalized.droppedPrefix)
            setOauthSetupClientId(clientId);
        setOauthSetupSaving(true);
        setOauthSetupError(null);
        try {
            await saveOAuthAppCredentials(oauthSetupAppId, clientId, clientSecret);
            setOauthAppsConfigured((prev) => {
                const next = new Set(prev);
                next.add(oauthSetupAppId.toLowerCase());
                return next;
            });
            const droppedNote = normalized.droppedPrefix
                ? ` Removed "${normalized.droppedPrefix}" that had been autofilled before the client ID.`
                : '';
            showToast('success', `${oauthSetupDisplayName || oauthSetupAppId} OAuth client saved for your account. Click Connect to finish.${droppedNote}`);
            closeOAuthSetup();
        }
        catch (err) {
            setOauthSetupError(err instanceof Error ? err.message : 'Failed to save credentials.');
        }
        finally {
            setOauthSetupSaving(false);
        }
    };
    const handleDisconnect = (appId: string) => {
        setConfirmingDisconnect(appId);
    };
    const handleCancelDisconnect = () => {
        setConfirmingDisconnect(null);
    };
    const handleConfirmDisconnect = async (appId: string) => {
        setDisconnectingApp(appId);
        const lowerId = appId.toLowerCase();
        setConnectedApps((prev) => prev.filter((a) => a.app.toLowerCase() !== lowerId && a.linked_app?.toLowerCase() !== lowerId));
        setConfirmingDisconnect(null);
        try {
            await composioClient.disconnectApp(appId);
            showToast('success', 'App disconnected successfully');
        }
        catch (err) {
            await loadConnectedApps();
            showToast('error', err instanceof Error ? err.message : 'Failed to disconnect app');
        }
        finally {
            setDisconnectingApp(null);
        }
    };
    const findConnectedApp = (appId: string): ConnectedApp | undefined => {
        return connectedApps.find((ca) => ca.app.toLowerCase() === appId.toLowerCase() ||
            ca.app_display_name?.toLowerCase() === appId.toLowerCase());
    };
    const connectedCount = connectedApps.filter((a) => a.status === 'active' && isAppVisible({ id: a.app })).length;
    return (<div className='intg-page'>
      <motion.div className='intg-container' initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        
        <button className='integrations-back-btn' onClick={() => navigate('/settings')}>
          <FiChevronLeft size={15}/> Back to Settings
        </button>

        
        <div className='intg-stats-bar'>
          <div className='intg-stats-bar__left'>
            <div className='intg-stat-chip intg-stat-chip--green'>
              <span className='intg-stat-chip__val'>{connectedCount}</span>
              <span className='intg-stat-chip__lbl'>Connected</span>
            </div>
            <div className='intg-stat-sep'/>
            <div className='intg-stat-chip intg-stat-chip--purple'>
              <span className='intg-stat-chip__val'>
                {availableApps.length}
              </span>
              <span className='intg-stat-chip__lbl'>Available</span>
            </div>
          </div>
          <button className='intg-refresh-btn' onClick={loadData} disabled={loading} title='Refresh integrations'>
            <FiRefreshCw size={14} className={loading ? 'spin' : ''}/>
            Refresh
          </button>
        </div>

        
        <AnimatePresence>
          {setupRequired && !loading && (<motion.div className='integrations-setup-banner' initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }}>
              <FiZap size={20} className='integrations-setup-banner__icon'/>
              <div className='integrations-setup-banner__body'>
                <strong>Request access to AI integrations</strong>
                <span>
                  Once enabled, connect Gmail, Calendar, and other tools your
                  agency uses every day. Your AI assistant can then send emails,
                  schedule meetings, and act on your behalf.
                </span>
              </div>
              <button className='integrations-setup-banner__cta' onClick={handleRequestAccess} disabled={creatingProject}>
                {creatingProject ? (<>
                    <FiRefreshCw size={14} className='spin'/> Setting up…
                  </>) : (<>
                    <FiZap size={14}/> Request Access
                  </>)}
              </button>
            </motion.div>)}
        </AnimatePresence>

        
        {error && (<div className='integrations-error'>
            <FiAlertTriangle size={18}/>
            <span>{error}</span>
            <button onClick={loadData}>Retry</button>
          </div>)}

        
        {loading && (<div className='integrations-loading'>
            <FiRefreshCw size={24} className='spin'/>
            <span>Loading integrations...</span>
          </div>)}

        
        {!loading &&
            (() => {
                const normalizeApp = (rawApp: AvailableApp): AvailableApp => rawApp.id === 'gte_voice' || rawApp.id === 'lightspeed_voice'
                    ? { ...rawApp, coming_soon: true, custom_setup: false }
                    : rawApp;
                const isMomentumApp = (a: AvailableApp) => a.id === 'momentum_voice' || a.id === 'momentum_email_exchange' || a.id === 'momentum_sms';
                const momentumApps = availableApps.filter(isMomentumApp);
                const thirdPartyApps = availableApps.filter((a) => !isMomentumApp(a));
                const renderCard = (rawApp: AvailableApp) => {
                    const app = normalizeApp(rawApp);
                    return (<AppCard key={app.id} app={app} connectedApp={findConnectedApp(app.id)} onConnect={handleConnect} onDisconnect={handleDisconnect} onConfirmDisconnect={handleConfirmDisconnect} onCancelDisconnect={handleCancelDisconnect} onToggleTrigger={handleToggleTrigger} onOpenOAuthSetup={openOAuthSetup} onOpenLsvSetup={openLsvModal} isConnecting={connectingApp === app.id} isDisconnecting={disconnectingApp === app.id} isConfirming={confirmingDisconnect === app.id} keyMissing={setupRequired} needsOAuthSetup={TOOLKITS_REQUIRING_OAUTH_SETUP.includes(app.id.toLowerCase()) && !oauthAppsConfigured.has(app.id.toLowerCase())} linkedAppName={app.linked_app ? (availableApps.find((a) => a.id === app.linked_app)?.name ?? app.linked_app) : undefined} linkedDependents={availableApps.filter((a) => a.linked_app === app.id).map((a) => a.name).join(', ') || undefined} isLsvSetup={app.custom_setup === true &&
                            app.id === 'lightspeed_voice' &&
                            !app.coming_soon} lsvConnected={lsvConnected} triggerEnabled={app.id === 'gmail' || app.id === 'outlook'
                            ? triggerStates[app.id as EmailTriggerProvider]
                            : false} isGteToggle={app.id === 'gte_voice' && !app.coming_soon} gteEnabled={gteEnabled} gteToggleLoading={gteToggleLoading && app.id === 'gte_voice'} onGteToggle={handleGteToggle} isMvSettings={app.id === 'momentum_voice'} mvEnabled={mvEnabled} mvToggleLoading={mvToggleLoading && app.id === 'momentum_voice'} onMvToggle={handleMvToggle} onOpenMvSettings={openMvModal} isMomentumEmailSettings={app.id === 'momentum_email_exchange'} momentumEmailConnected={momentumEmailConnected} momentumEmailError={momentumEmailError} onOpenMomentumEmailSettings={openMomentumEmailModal} isSmsToggle={app.id === 'momentum_sms'} smsEnabled={smsEnabled} smsToggleLoading={smsToggleLoading && app.id === 'momentum_sms'} onSmsToggle={handleSmsToggle} onOpenSmsActivity={() => setActivityModalSource('sms')} triggerLoading={triggerLoadingProvider === app.id}/>);
                };
                return (<>
                
                {momentumApps.length > 0 && (<section className='integrations-section'>
                    <div className='integrations-section__header'>
                      <h2 className='integrations-section__title'>
                        Momentum Apps
                      </h2>
                      <p className='integrations-section__subtitle'>
                        Native integrations built into Momentum.
                      </p>
                    </div>
                    <div className='integrations-grid'>
                      <AnimatePresence>
                        {momentumApps.map(renderCard)}
                      </AnimatePresence>
                    </div>
                  </section>)}

                
                {thirdPartyApps.length > 0 && (<section className='integrations-section'>
                    <div className='integrations-section__header'>
                      <h2 className='integrations-section__title'>
                        Third-Party Apps
                      </h2>
                      <p className='integrations-section__subtitle'>
                        Connect the external tools your agency uses every day.
                      </p>
                    </div>
                    <div className='integrations-grid'>
                      <AnimatePresence>
                        {thirdPartyApps.map(renderCard)}
                      </AnimatePresence>
                    </div>
                  </section>)}
              </>);
            })()}

        
        {anyTriggerEnabled &&
            triggerEvents.length > 0 &&
            (() => {
                const incomingEvents = triggerEvents.filter((event) => {
                    const p = event.payload as Record<string, unknown>;
                    const raw = p?.label_ids ?? p?.labelIds;
                    if (!raw)
                        return true;
                    const labels = Array.isArray(raw)
                        ? (raw as string[])
                        : String(raw)
                            .split(',')
                            .map((l) => l.trim());
                    return !labels.includes('SENT');
                });
                if (incomingEvents.length === 0)
                    return null;
                return (<section className='integrations-events'>
                <div className='integrations-events__header'>
                  <FiBell size={16} className='integrations-events__header-icon'/>
                  <h3>Recent Email Activity</h3>
                  <span className='integrations-events__badge'>
                    {incomingEvents.length}
                  </span>
                  <button className='integrations-events__refresh' onClick={() => loadTriggerEvents(true)} disabled={eventsLoading} title='Refresh'>
                    <FiRefreshCw size={13} className={eventsLoading ? 'spin' : ''}/>
                  </button>
                </div>

                <div className='integrations-events__list'>
                  {incomingEvents.map((event) => {
                        const p = event.payload as Record<string, unknown>;
                        const preview = p?.preview && typeof p.preview === 'object'
                            ? (p.preview as Record<string, unknown>)
                            : null;
                        const toStr = (v: unknown) => v && typeof v === 'string' ? v : '';
                        const from = toStr(p?.from) ||
                            toStr(p?.sender) ||
                            toStr(p?.fromAddress) ||
                            toStr(p?.senderEmail) ||
                            'Unknown sender';
                        const subject = toStr(p?.subject) ||
                            toStr(p?.emailSubject) ||
                            toStr(p?.title) ||
                            toStr(preview?.subject) ||
                            'No subject';
                        const rawSnippet = toStr(p?.snippet) ||
                            toStr(p?.message_text) ||
                            toStr(p?.messageText) ||
                            toStr(p?.body_preview) ||
                            toStr(p?.messagePreview) ||
                            toStr(p?.body) ||
                            toStr(preview?.body) ||
                            toStr(preview?.text);
                        const snippet = rawSnippet
                            .replace(/\s+/g, ' ')
                            .trim()
                            .slice(0, 120);
                        const emailDateRaw = toStr(p?.message_timestamp) ||
                            toStr(p?.date) ||
                            toStr(p?.messageDate) ||
                            toStr(p?.sentAt) ||
                            toStr(p?.receivedAt);
                        const displayDate = emailDateRaw
                            ? emailDateRaw
                            : event.received_at;
                        return (<div key={event.id} className='integrations-events__item'>
                        <div className='integrations-events__item-icon'>📧</div>
                        <div className='integrations-events__item-body'>
                          <div className='integrations-events__item-from'>
                            {from}
                          </div>
                          <div className='integrations-events__item-subject'>
                            {subject}
                          </div>
                          {snippet && (<div className='integrations-events__item-snippet'>
                              {snippet}
                            </div>)}
                        </div>
                        <div className='integrations-events__item-time'>
                          {formatRelativeTime(displayDate)}
                        </div>
                      </div>);
                    })}
                </div>
              </section>);
            })()}

        {/* Activity — opened from the Email Exchange / Voice settings modals, so
            each one shows only its own integration's history. */}
        <AnimatePresence>
          {activityModalSource && (<IntegrationActivityModal source={activityModalSource} title={activityModalSource === 'email' ? 'Momentum email exchange' : activityModalSource === 'sms' ? 'Momentum SMS' : 'Momentum Voice'} onClose={() => setActivityModalSource(null)}/>)}
        </AnimatePresence>


        <AnimatePresence>
          {oauthSetupAppId && (<motion.div className='composio-project-modal' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeOAuthSetup}>
              <motion.div className='composio-project-modal__panel composio-project-modal__panel--wide' role='dialog' aria-modal='true' aria-labelledby='oauth-setup-modal-title' initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }} onClick={(e) => e.stopPropagation()}>
                <div className='composio-project-modal__header'>
                  <FiLock size={18}/>
                  <h3 id='oauth-setup-modal-title'>
                    Set up {oauthSetupDisplayName} OAuth
                  </h3>
                  <button type='button' className='composio-project-modal__close' onClick={closeOAuthSetup} disabled={oauthSetupSaving} aria-label='Close'>
                    <FiX size={16}/>
                  </button>
                </div>

                <p className='composio-project-modal__desc'>
                  {oauthSetupDisplayName} is not covered by Composio&apos;s managed
                  OAuth, so, just like your Gmail connection, it is set up for your
                  account only. Paste the {oauthSetupGuide.clientIdLabel} and{' '}
                  {oauthSetupGuide.clientSecretLabel} from your {oauthSetupGuide.consoleHint}.
                  Credentials are stored encrypted and used by your account alone.
                  After saving, click Connect to link your {oauthSetupDisplayName} account.
                </p>

                <div className='composio-project-modal__label'>
                  How to get the {oauthSetupGuide.clientIdLabel} and {oauthSetupGuide.clientSecretLabel}
                </div>
                <ol className='composio-project-modal__desc composio-project-modal__steps'>
                  {oauthSetupGuide.steps.map((step) => (<li key={step}>{step}</li>))}
                </ol>

                <div className='composio-project-modal__label'>
                  Redirect URIs to register on the OAuth client (add both)
                </div>
                {COMPOSIO_OAUTH_CALLBACK_URLS.map((url, index) => (<div className='lsv-webhook-row' key={url}>
                    <div className='lsv-webhook-url'>
                      {url}
                    </div>
                    <button type='button' className='integration-btn integration-btn--connect lsv-copy-btn composio-project-modal__copy-btn' onClick={() => handleCopyOAuthRedirect(url, index)} aria-label={`Copy ${url}`}>
                      {oauthRedirectCopiedIndex === index ? (<>
                          <FiCheck size={13}/> Copied
                        </>) : (<>
                          <FiCopy size={13}/> Copy
                        </>)}
                    </button>
                  </div>))}

                <label className='composio-project-modal__label' htmlFor='oauth-client-id'>
                  {oauthSetupGuide.clientIdLabel}
                </label>
                <div className='composio-project-modal__input-wrap'>
                  {/* Not a login form: no type="password" (it makes browsers autofill the saved
                      username into the field above) and password managers are told to ignore it. */}
                  <input id='oauth-client-id' name='oauth-client-id' className='composio-project-modal__input' placeholder={oauthSetupGuide.clientIdPlaceholder} value={oauthSetupClientId} autoFocus onChange={(e) => {
                setOauthSetupClientId(e.target.value);
                if (oauthSetupError)
                    setOauthSetupError(null);
            }} disabled={oauthSetupSaving} autoComplete='off' spellCheck={false} data-lpignore='true' data-1p-ignore='true' data-form-type='other'/>
                </div>

                <label className='composio-project-modal__label' htmlFor='oauth-client-secret'>
                  {oauthSetupGuide.clientSecretLabel}
                </label>
                <div className='composio-project-modal__input-wrap'>
                  <input id='oauth-client-secret' name='oauth-client-secret' type='text' className='composio-project-modal__input composio-project-modal__input--secret' placeholder={oauthSetupGuide.clientSecretPlaceholder} value={oauthSetupClientSecret} spellCheck={false} data-lpignore='true' data-1p-ignore='true' data-form-type='other' onChange={(e) => {
                setOauthSetupClientSecret(e.target.value);
                if (oauthSetupError)
                    setOauthSetupError(null);
            }} onKeyDown={(e) => {
                if (e.key === 'Enter' && !oauthSetupSaving) {
                    e.preventDefault();
                    void handleSaveOAuthSetup();
                }
            }} disabled={oauthSetupSaving} autoComplete='off'/>
                </div>

                {oauthSetupError && (<div className='composio-project-modal__error' role='alert'>
                    <FiAlertTriangle size={14}/>
                    <span>{oauthSetupError}</span>
                  </div>)}

                <div className='composio-project-modal__actions'>
                  <button type='button' className='integration-btn integration-btn--confirm-no' onClick={closeOAuthSetup} disabled={oauthSetupSaving}>
                    Cancel
                  </button>
                  <button type='button' className='integration-btn integration-btn--connect' onClick={() => void handleSaveOAuthSetup()} disabled={oauthSetupSaving ||
                !oauthSetupClientId.trim() ||
                !oauthSetupClientSecret.trim()}>
                    {oauthSetupSaving ? (<>
                        <FiRefreshCw size={14} className='spin'/> Saving...
                      </>) : (<>
                        <FiCheck size={14}/> Save Credentials
                      </>)}
                  </button>
                </div>
              </motion.div>
            </motion.div>)}
        </AnimatePresence>

        
        <AnimatePresence>
          {lsvModalOpen && (<motion.div className='composio-project-modal' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeLsvModal}>
              <motion.div className='composio-project-modal__panel lsv-modal__panel' role='dialog' aria-modal='true' aria-labelledby='lsv-modal-title' initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }} onClick={(e) => e.stopPropagation()}>
                
                <div className='composio-project-modal__header'>
                  <img width='22' height='22' src='https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://lightspeedvoice.com&size=128' alt='Lightspeed Voice' style={{ borderRadius: 4 }}/>
                  <h3 id='lsv-modal-title'>Lightspeed Voice Setup</h3>
                  <button type='button' className='composio-project-modal__close' onClick={closeLsvModal} disabled={lsvSaving || lsvRemoving} aria-label='Close'>
                    <FiX size={16}/>
                  </button>
                </div>

                {lsvLoading ? (<div className='lsv-modal__loading'>
                    <FiRefreshCw size={22} className='spin'/>
                    <span>Loading settings…</span>
                  </div>) : (<>
                    
                    <div className='lsv-modal__step'>
                      <div className='lsv-modal__step-heading'>
                        <span className='lsv-modal__step-num'>1</span>
                        <span>Enter your Lightspeed Voice credentials</span>
                      </div>
                      <p className='composio-project-modal__desc'>
                        Find your token in Lightspeed Voice under{' '}
                        <strong>
                          Management → Lead Settings → Integrations
                        </strong>
                        . Click the eye icon next to your integration to reveal
                        the token.
                      </p>

                      
                      <label className='composio-project-modal__label' htmlFor='lsv-email'>
                        LSV Email <span className='lsv-required'>*</span>
                      </label>
                      <div className='composio-project-modal__input-wrap'>
                        <input id='lsv-email' className={`composio-project-modal__input${lsvEmailError ? ' lsv-input--error' : ''}`} placeholder='Enter your Lightspeed Voice email' value={lsvEmail} autoComplete='off' disabled={lsvSaving || lsvRemoving} onChange={(e) => {
                    setLsvEmail(e.target.value);
                    setLsvEmailError('');
                }} onKeyDown={(e) => {
                    if (e.key === 'Enter')
                        void handleLsvSave();
                }}/>
                      </div>
                      {lsvEmailError && (<div className='lsv-field-error'>
                          <FiAlertTriangle size={12}/>
                          {lsvEmailError}
                        </div>)}

                      
                      <label className='composio-project-modal__label' htmlFor='lsv-token' style={{ marginTop: 12 }}>
                        LSV Token <span className='lsv-required'>*</span>
                      </label>
                      <div className='composio-project-modal__input-wrap'>
                        <input id='lsv-token' type={lsvShowToken ? 'text' : 'password'} className={`composio-project-modal__input${lsvTokenError ? ' lsv-input--error' : ''}`} placeholder='Enter your Lightspeed Voice token' value={lsvToken} autoComplete='new-password' disabled={lsvSaving || lsvRemoving} onChange={(e) => {
                    setLsvToken(e.target.value);
                    setLsvTokenError('');
                }} onKeyDown={(e) => {
                    if (e.key === 'Enter')
                        void handleLsvSave();
                }}/>
                        <button type='button' className='lsv-eye-btn' onClick={() => setLsvShowToken((v) => !v)} tabIndex={-1} aria-label={lsvShowToken ? 'Hide token' : 'Show token'}>
                          {lsvShowToken ? (<FiEyeOff size={14}/>) : (<FiEye size={14}/>)}
                        </button>
                      </div>
                      {lsvTokenError && (<div className='lsv-field-error'>
                          <FiAlertTriangle size={12}/>
                          {lsvTokenError}
                        </div>)}

                      <div className='composio-project-modal__actions' style={{ marginTop: 14 }}>
                        {lsvConnected && (<button type='button' className='integration-btn integration-btn--confirm-yes' onClick={handleLsvRemove} disabled={lsvSaving || lsvRemoving}>
                            {lsvRemoving ? (<>
                                <FiRefreshCw size={14} className='spin'/>{' '}
                                Removing…
                              </>) : (<>
                                <FiX size={14}/> Remove
                              </>)}
                          </button>)}
                        <button type='button' className='integration-btn integration-btn--connect' onClick={handleLsvSave} disabled={lsvSaving || lsvRemoving}>
                          {lsvSaving ? (<>
                              <FiRefreshCw size={14} className='spin'/> Saving…
                            </>) : (<>
                              <FiCheck size={14}/> Save
                            </>)}
                        </button>
                      </div>
                    </div>

                    <div className='lsv-modal__divider'/>

                    
                    <div className='lsv-modal__step'>
                      <div className='lsv-modal__step-heading'>
                        <span className='lsv-modal__step-num'>2</span>
                        <span>Register the webhook with Lightspeed Voice</span>
                      </div>
                      <p className='composio-project-modal__desc'>
                        Email <strong>help@lightspeedvoice.com</strong> and ask
                        them to send call webhooks to the URL below. Lightspeed
                        Voice will reply with an <strong>API-KEY</strong> —
                        paste it in the field below when you receive it.
                      </p>

                      <label className='composio-project-modal__label'>
                        Webhook URL
                      </label>
                      <div className='lsv-webhook-row'>
                        <div className='lsv-webhook-url'>
                          {LIGHTSPEED_WEBHOOK_URL}
                        </div>
                        <button type='button' className='integration-btn integration-btn--connect lsv-copy-btn' onClick={handleLsvCopyWebhook}>
                          {lsvWebhookCopied ? (<>
                              <FiCheck size={13}/> Copied
                            </>) : (<>
                              <FiCopy size={13}/> Copy
                            </>)}
                        </button>
                      </div>

                      <div className='lsv-info-banner'>
                        <span>ℹ️</span>
                        <span>
                          Once Lightspeed Voice confirms the webhook, every call
                          that ends will automatically be sent here for AI
                          transcript and analysis.
                        </span>
                      </div>

                      <label className='composio-project-modal__label' htmlFor='lsv-apikey' style={{ marginTop: 14 }}>
                        API Key <span className='lsv-required'>*</span>
                        <span className='lsv-label-hint'>
                          {' '}
                          — received from Lightspeed Voice
                        </span>
                      </label>
                      <div className='composio-project-modal__input-wrap'>
                        <input id='lsv-apikey' type='password' className='composio-project-modal__input' placeholder='Paste the API Key from Lightspeed Voice' value={lsvApiKey} autoComplete='new-password' disabled={lsvSaving || lsvRemoving} onChange={(e) => setLsvApiKey(e.target.value)}/>
                      </div>

                      <div className='composio-project-modal__actions' style={{ marginTop: 14 }}>
                        <button type='button' className='integration-btn integration-btn--connect' onClick={handleLsvSaveApiKey} disabled={lsvSaving ||
                    lsvRemoving ||
                    !lsvApiKey.trim() ||
                    lsvApiKey === '••••••••••••'}>
                          {lsvSaving ? (<>
                              <FiRefreshCw size={14} className='spin'/> Saving…
                            </>) : (<>
                              <FiCheck size={14}/> Save API Key
                            </>)}
                        </button>
                      </div>
                    </div>

                    {lsvError && (<div className='composio-project-modal__error' role='alert'>
                        <FiAlertTriangle size={14}/>
                        <span>{lsvError}</span>
                      </div>)}
                  </>)}
              </motion.div>
            </motion.div>)}
        </AnimatePresence>

        
        <AnimatePresence>
          {mvModalOpen && (<motion.div className='composio-project-modal' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeMvModal}>
              <motion.div className='composio-project-modal__panel' role='dialog' aria-modal='true' aria-labelledby='mv-modal-title' initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }} onClick={(e) => e.stopPropagation()} style={{ maxHeight: '85vh', overflowY: 'auto' }}>
                <div className='composio-project-modal__header'>
                  <FiSettings size={18}/>
                  <h3 id='mv-modal-title'>Momentum Voice — Agents</h3>
                  <button type='button' className='composio-project-modal__close' onClick={closeMvModal} disabled={!!mvSavingAgentId} aria-label='Close'>
                    <FiX size={16}/>
                  </button>
                </div>

                <p className='composio-project-modal__desc'>
                  Choose which voice agents to integrate. Calls handled by an
                  enabled agent will have their summary sent to your Kanban
                  automatically.
                </p>

                {mvLoading ? (<div className='lsv-modal__loading'>
                    <FiRefreshCw size={22} className='spin'/>
                    <span>Loading agents…</span>
                  </div>) : mvError ? (<div className='composio-project-modal__error' role='alert'>
                    <FiAlertTriangle size={14}/>
                    <span>{mvError}</span>
                  </div>) : mvAgents.length === 0 ? (<div className='lsv-info-banner'>
                    <span>ℹ️</span>
                    <span>No voice agents found for your agency yet.</span>
                  </div>) : (<div style={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    maxHeight: '50vh',
                    overflowY: 'auto',
                    paddingRight: 4,
                }}>
                    {mvAgents.map((agent) => (<div key={agent.id} className='integration-card__trigger' style={{ marginTop: 0 }}>
                        <div className='integration-card__trigger-info'>
                          <FiBell size={13}/>
                          <span>{agent.name}</span>
                        </div>
                        <button className={`integration-trigger-toggle ${agent.enabled ? 'integration-trigger-toggle--on' : ''}`} onClick={() => handleMvAgentToggle(agent.id, !agent.enabled)} disabled={mvSavingAgentId === agent.id} title={agent.enabled ? 'Disable agent' : 'Enable agent'}>
                          {mvSavingAgentId === agent.id ? (<FiRefreshCw size={12} className='spin'/>) : agent.enabled ? (<>
                              <span className='integration-trigger-toggle__dot'/>{' '}
                              On
                            </>) : (<>
                              <span className='integration-trigger-toggle__dot'/>{' '}
                              Off
                            </>)}
                        </button>
                      </div>))}
                  </div>)}

                <div className='composio-project-modal__actions' style={{ marginTop: 14 }}>
                  <button type='button' className='integration-btn' onClick={() => setActivityModalSource('voice')}>
                    <FiActivity size={14}/> View activity
                  </button>
                  <button type='button' className='integration-btn integration-btn--connect' onClick={closeMvModal} disabled={!!mvSavingAgentId}>
                    <FiCheck size={14}/> Done
                  </button>
                </div>
              </motion.div>
            </motion.div>)}
        </AnimatePresence>

        
        <AnimatePresence>
          {momentumEmailModalOpen && (<motion.div className='composio-project-modal' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeMomentumEmailModal}>
              <motion.div className='composio-project-modal__panel' role='dialog' aria-modal='true' aria-labelledby='momentum-email-modal-title' initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }} onClick={(e) => e.stopPropagation()} style={{ maxHeight: '85vh', overflowY: 'auto' }}>
                <div className='composio-project-modal__header'>
                  <FiSettings size={18}/>
                  <h3 id='momentum-email-modal-title'>
                    Momentum email exchange — Connected Integrations
                  </h3>
                  <button type='button' className='composio-project-modal__close' onClick={closeMomentumEmailModal} disabled={momentumEmailSavingId !== null} aria-label='Close'>
                    <FiX size={16}/>
                  </button>
                </div>

                <p className='composio-project-modal__desc'>
                  Email accounts connected to your agency. Toggle AI automation
                  on or off for each integration.
                </p>

                {momentumEmailLoading ? (<div className='lsv-modal__loading'>
                    <FiRefreshCw size={22} className='spin'/>
                    <span>Loading integrations…</span>
                  </div>) : momentumEmailNotConfigured ? (<div className='lsv-info-banner' role='status'>
                    <span>ℹ️</span>
                    <span>
                      Momentum email exchange is not configured for your account
                      yet. Please contact your administrator to set up email
                      integration.
                    </span>
                  </div>) : momentumEmailError ? (<div className='composio-project-modal__error' role='alert'>
                    <FiAlertTriangle size={14}/>
                    <span>{momentumEmailError}</span>
                  </div>) : momentumEmailIntegrations.length === 0 ? (<div className='lsv-info-banner'>
                    <span>ℹ️</span>
                    <span>No email integrations connected yet.</span>
                  </div>) : (<div style={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    maxHeight: '50vh',
                    overflowY: 'auto',
                    paddingRight: 4,
                }}>
                    {supportedMomentumEmailIntegrations.length === 0 && (<div className='lsv-info-banner'>
                        <span>ℹ️</span>
                        <span>No connected account can use AI automation. Only the Momentum MAC default email integration can.</span>
                      </div>)}
                    {supportedMomentumEmailIntegrations.map(renderMomentumEmailRow)}
                    {unsupportedMomentumEmailIntegrations.length > 0 && (<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <button type='button' onClick={() => setMomentumEmailShowUnsupported((v) => !v)} aria-expanded={momentumEmailShowUnsupported} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: 0,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--text-secondary)',
                    }}>
                          {momentumEmailShowUnsupported ? <FiChevronDown size={14}/> : <FiChevronRight size={14}/>}
                          Not supported for AI ({unsupportedMomentumEmailIntegrations.length})
                        </button>
                        {momentumEmailShowUnsupported && (<>
                            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                              Connected in Momentum MAC for another purpose (e.g. support). AI automation works only with the MAC default email integration.
                            </span>
                            {unsupportedMomentumEmailIntegrations.map(renderMomentumEmailRow)}
                          </>)}
                      </div>)}
                  </div>)}

                <div className='composio-project-modal__actions' style={{ marginTop: 14 }}>
                  <button type='button' className='integration-btn' onClick={() => setActivityModalSource('email')}>
                    <FiActivity size={14}/> View activity
                  </button>
                  <button type='button' className='integration-btn integration-btn--connect' onClick={closeMomentumEmailModal} disabled={momentumEmailSavingId !== null}>
                    <FiCheck size={14}/> Done
                  </button>
                </div>
              </motion.div>
            </motion.div>)}
        </AnimatePresence>

        
        <section className='intg-how'>
          <div className='intg-how__label'>
            <span className='s2-section-dot s2-section-dot--purple'/>
            How it works
          </div>
          <div className='intg-how__steps'>
            {[
            {
                n: '1',
                text: 'Click "Connect" on an app to authorize access',
                color: '#8b5cf6',
                bg: 'rgba(139,92,246,0.12)',
            },
            {
                n: '2',
                text: 'Complete the OAuth sign-in in the secure popup',
                color: '#22d3ee',
                bg: 'rgba(34,211,238,0.12)',
            },
            {
                n: '3',
                text: 'Your AI assistant can now automate tasks on your behalf',
                color: '#34d399',
                bg: 'rgba(52,211,153,0.12)',
            },
        ].map((s) => (<div key={s.n} className='intg-how__step'>
                <div className='intg-how__step-num' style={{
                background: s.bg,
                color: s.color,
                border: `1px solid ${s.color}40`,
            }}>
                  {s.n}
                </div>
                <p>{s.text}</p>
              </div>))}
          </div>
        </section>
      </motion.div>

      
      <AnimatePresence>
        {toastMessage && (<motion.div className={`integrations-toast integrations-toast--${toastMessage.type}`} initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }}>
            {toastMessage.type === 'success' ? (<FiCheck size={18}/>) : (<FiAlertTriangle size={18}/>)}
            <span>{toastMessage.message}</span>
            <button onClick={() => setToastMessage(null)}>
              <FiX size={16}/>
            </button>
          </motion.div>)}
      </AnimatePresence>
    </div>);
};
export default IntegrationsPage;
